// Generic multi-select sessions on the pile-search dialog.
//
// Card effects that pick N cards from a pile (cemetery, banished,
// atlas, spellbook) share one interaction model:
//   • clicking a tile toggles its selection (highlight + ✕ badge)
//   • right-clicking a selected tile unselects it
//   • an explicit Accept button commits the action. Nothing mutates
//     before that click
//   • closing the dialog (ESC / ✕ / click-outside) aborts for free
//
// A session is plain JSON state in `host.state.pendingPileSelect`:
//   {
//     kind,            // dispatch key into KINDS below
//     pileId, controller, sourceCardId, sourceName,
//     title, hint, confirmLabel,
//     max,             // selection cap (Accept needs exactly `max`)
//     selectedIds: [],
//     eligibleIds,     // optional allowlist; other tiles dim out
//     meta,            // kind-specific payload (carried across stages)
//   }
//
// Adding a new pile-pick effect = register a kind: a `validate`
// predicate (which tiles are selectable) and a `confirm` handler
// (commit, or chain into a follow-up session for multi-stage flows).
// No dialog or renderer changes needed.

import { toast } from 'sonner';
import { groupPicksByDeck } from '../../../utils/game/engine/automation/ops/cemeteryDeckOps';
import { partitionWindow, wouldDuplicateName } from '../../../utils/game/engine/automation/ops/cards/blackMass';
import { namedCemeterySpells } from '../../../utils/game/engine/automation/ops/cards/sphinxBanishPair';
import { finishFlankingSubset } from './automationRuntime';
import { resolveServerPileChoice } from './authChoice';
import { k, t } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';

// Every pile a session draws from. Single-pile sessions just use
// `pileId`; cemetery-spanning sessions (Return to Nature, Sphinx) list
// both Cemetery piles in `pileIds` so a pick can come from either side.
// The dialog flips `searchPile` between them via the cemetery tabs while
// `pendingPileSelect` (incl. selectedIds) stays untouched.
function sessionPiles(host, session) {
  const ids = session.pileIds || [session.pileId];
  const piles = host.state.gameState.piles;
  return ids.map((id) => piles.find((p) => p.id === id)).filter(Boolean);
}

// Pure selection reducer. Toggles `cardId` within the cap. Returns
// the new array, or null when the click should be rejected (cap hit).
export function toggleSelection(selectedIds, cardId, max) {
  if (selectedIds.includes(cardId)) {
    return selectedIds.filter((id) => id !== cardId);
  }
  if (selectedIds.length >= max) return null;
  return [...selectedIds, cardId];
}

const KINDS = {
  // Server-authoritative pile pick (pendingChoice kind:'pile'. Pendragon
  // Legacy / Return to Nature under auth). The SERVER computed the allowlist
  // (this session's eligibleIds); confirm replies with the picked ids via
  // choosePileIntent and the server commits the pile mutations (the diff
  // renders them). Nothing mutates locally.
  server_pile_choice: {
    validate(host, card, session) {
      const cards = sessionPiles(host, session).flatMap((p) => p.cards);
      // Pair prompts (Sphinx Sarcophagus) must end on two IDENTICAL names.
      // once a first pick is in, only its twins stay selectable. The server
      // re-validates the committed pair regardless.
      if (session.meta?.pair) {
        const first = session.selectedIds[0];
        if (!first) return true;
        const firstCard = cards.find((c) => c.id === first);
        return !firstCard || (firstCard.name || null) === (card.name || null);
      }
      // Distinct-name prompts (Black Mass) reject a pick that duplicates an
      // already-selected name. The server re-validates the commit regardless.
      if (session.meta?.distinctNames) {
        const name = (card.name || '').toLowerCase();
        return !session.selectedIds.some((id) => {
          const other = cards.find((c) => c.id === id);
          return other && (other.name || '').toLowerCase() === name;
        });
      }
      return true;
    },
    invalidMessage: k('That name is already picked.'),
    confirm(host, session) {
      resolveServerPileChoice(host, session, [...session.selectedIds]);
    },
  },
  // Xeraphine Konrul, stage 1: "choose three of your dead minions".
  xeraphine_choose: {
    validate(host, card) {
      const full = host._getFullCard(card.cardId);
      return (full?.type ?? card.type) === 'Minion';
    },
    invalidMessage: k('Pick a minion.'),
    confirm(host, session) {
      // Stage 2: pick which of the chosen returns to hand. Same
      // session machinery. The stage-1 picks become the allowlist.
      host.setState({
        pendingPileSelect: {
          ...session,
          kind: 'xeraphine_return',
          title: t('Return which minion to hand?'),
          hint: t('The other two banish'),
          confirmLabel: t('Banish 2, return 1'),
          max: 1,
          eligibleIds: [...session.selectedIds],
          selectedIds: [],
          meta: { chosenIds: [...session.selectedIds] },
        },
      });
    },
  },
  // Xeraphine Konrul, stage 2: commit the rites.
  xeraphine_return: {
    validate() { return true; }, // eligibility allowlist already gates
    confirm(host, session) {
      const returnId = session.selectedIds[0];
      const pile = host.state.gameState.piles.find((p) => p.id === session.pileId);
      if (!pile || !returnId) {
        endSession(host);
        return;
      }
      const banishedPile = host.findOrCreateBanished(session.controller === 'p2');
      const banishedNames = [];
      let returnCard = null;
      for (const id of session.meta.chosenIds) {
        const idx = pile.cards.findIndex((c) => c.id === id);
        if (idx === -1) continue;
        if (id === returnId) {
          returnCard = pile.cards[idx];
          continue;
        }
        const [removed] = pile.cards.splice(idx, 1);
        banishedNames.push(removed.name || 'a minion');
        banishedPile.cards.push(removed);
      }
      host.updatePileMeshes();
      host.logAction('{source}: banishes {names}, returns {card} to hand', {
        source: session.sourceName,
        names: banishedNames.join(' and '),
        card: returnCard?.name || msg('a minion'),
      });
      endSession(host, () => {
        // silent: the summary above already names the returned minion.
        if (returnCard) host.takeCardFromPile(pile, returnCard, { silent: true });
      });
    },
  },
  // Pendragon Legacy: shuffle the picked cards back into the decks they
  // belong to (Atlas for Sites, Spellbook otherwise), then shuffle
  // those decks. Eligibility is gated by the session's allowlist, so
  // validate trivially accepts.
  shuffle_cemetery_into_deck: {
    validate() { return true; },
    confirm(host, session) {
      const pile = host.state.gameState.piles.find((p) => p.id === session.pileId);
      if (!pile) { endSession(host); return; }
      const playerNum = session.controller === 'p2' ? 2 : 1;
      const groups = groupPicksByDeck(pile.cards, session.selectedIds, host._getFullCard);
      const movedNames = [];
      const touchedDecks = [];
      for (const [deckName, cards] of Object.entries(groups)) {
        const deck = host.findPileByNameForPlayer(deckName, playerNum);
        if (!deck) continue;
        for (const card of cards) {
          const idx = pile.cards.findIndex((c) => c.id === card.id);
          if (idx === -1) continue;
          const [removed] = pile.cards.splice(idx, 1);
          deck.cards.push(removed);
          movedNames.push(removed.name || 'a card');
        }
        touchedDecks.push(deck);
      }
      for (const deck of touchedDecks) host.shufflePileAction(deck);
      host.updatePileMeshes();
      if (movedNames.length > 0) {
        host.logAction('{source}: shuffles {names} into the decks', { source: session.sourceName, names: movedNames.join(', ') });
      }
      endSession(host);
    },
  },
  // Black Mass: the dialog is opened on a WINDOW of the spellbook's top
  // seven only (never the whole library). Pick up to three DIFFERENT
  // (different-named) Evil minions to draw, then the rest of the seven
  // go to the bottom of the spellbook in window order. Eligibility (Evil
  // minions) is the session's allowlist; `validate` enforces only the
  // distinct-name rule. `meta.windowIds` is the full seven so the commit
  // knows which leftovers to bottom-out.
  black_mass_window: {
    validate(host, card, session) {
      if (wouldDuplicateName(session.meta.windowCards, session.selectedIds, card, host._getFullCard)) {
        return false;
      }
      return true;
    },
    invalidMessage: k('Pick a DIFFERENT Evil minion (no two of the same name).'),
    confirm(host, session) {
      const pile = host.state.gameState.piles.find((p) => p.id === session.pileId);
      if (!pile) { endSession(host); return; }
      const windowCards = (session.meta.windowIds || [])
        .map((id) => pile.cards.find((c) => c.id === id))
        .filter(Boolean);
      const { picked, rest } = partitionWindow(windowCards, session.selectedIds);
      // Bottom-out the leftovers FIRST so the draw's pile:sync reflects
      // the final order. Top is the last element, bottom is index 0;
      // unshift in window order keeps the leftovers' relative order.
      for (let i = rest.length - 1; i >= 0; i--) {
        const card = rest[i];
        const idx = pile.cards.findIndex((c) => c.id === card.id);
        if (idx === -1) continue;
        const [removed] = pile.cards.splice(idx, 1);
        pile.cards.unshift(removed);
      }
      host.updatePileMeshes();
      // Draw the picks through the canonical pile → hand primitive (the
      // same one SEARCH_DECK uses), which reveals nothing. These stay
      // the controller's private draws until they choose to reveal.
      const drawnNames = [];
      for (const card of picked) {
        // silent: the single summary below replaces one line per pick.
        host.takeCardFromPile(pile, card, { silent: true });
        drawnNames.push(card.name || 'a minion');
      }
      if (drawnNames.length > 0) {
        host.logAction('{source}: draws {names}; rest to the bottom of the spellbook', { source: session.sourceName, names: drawnNames.join(', ') });
      } else {
        host.logAction('{source}: draws nothing; the seven go to the bottom of the spellbook', { source: session.sourceName });
      }
      endSession(host);
    },
  },
  // Return to Nature: return each picked card to the BOTTOM of the deck
  // it belongs to. "From a cemetery" spans EITHER player's Cemetery, so
  // a card returns to ITS OWNER's deck. Keyed off the cemetery pile it
  // came from (pile.rotated), not the controller. No shuffle. Top of a
  // pile is the LAST element, so the bottom is index 0 (unshift).
  return_cemetery_to_deck_bottom: {
    validate() { return true; },
    confirm(host, session) {
      const piles = sessionPiles(host, session);
      if (piles.length === 0) { endSession(host); return; }
      const movedNames = [];
      const touched = new Set();
      for (const pile of piles) {
        const playerNum = pile.rotated ? 2 : 1;
        const groups = groupPicksByDeck(pile.cards, session.selectedIds, host._getFullCard);
        for (const [deckName, cards] of Object.entries(groups)) {
          const deck = host.findPileByNameForPlayer(deckName, playerNum);
          if (!deck) continue;
          for (const card of cards) {
            const idx = pile.cards.findIndex((c) => c.id === card.id);
            if (idx === -1) continue;
            const [removed] = pile.cards.splice(idx, 1);
            deck.cards.unshift(removed);
            movedNames.push(removed.name || 'a card');
          }
          touched.add(deck);
        }
        touched.add(pile);
      }
      host.updatePileMeshes();
      if (movedNames.length > 0) {
        host.logAction('{source}: returns {names} to the bottom of the decks', { source: session.sourceName, names: movedNames.join(', ') });
      }
      endSession(host);
    },
  },
  // Sphinx Sarcophagus Genesis: banish two identically-named spells from
  // EITHER cemetery, then draw a spell. The pick spans both Cemetery
  // piles (session.pileIds). `validate` only admits a spell that still
  // has an unselected same-named twin across the cemeteries (so an
  // unmatchable card can never be the first or second pick). `confirm`
  // banishes both copies. Each into ITS OWNER's Banished pile. And
  // fires the draw via meta.drawArgs.
  sphinx_banish_pair: {
    validate(host, card, session) {
      const rows = namedCemeterySpells(sessionPiles(host, session), host.props.sorceryCards || []);
      const row = rows.find((r) => r.card.id === card.id);
      if (!row) return false;
      // A spell is selectable iff a same-named copy exists somewhere in
      // the cemeteries. That copy IS its pair (and may be the one already
      // selected as the first pick). When the first pick is already in,
      // additionally require the new pick to share that pick's name, so
      // the two banished copies are always identical.
      const twin = rows.some((r) => r.name === row.name && r.card.id !== card.id);
      if (!twin) return false;
      const first = session.selectedIds[0];
      if (first) {
        const firstRow = rows.find((r) => r.card.id === first);
        if (firstRow && firstRow.name !== row.name) return false;
      }
      return true;
    },
    invalidMessage: k('Pick a spell with an identical twin in the cemeteries.'),
    confirm(host, session) {
      const piles = sessionPiles(host, session);
      const rows = namedCemeterySpells(piles, host.props.sorceryCards || []);
      const banishedNames = [];
      const touched = new Set();
      for (const id of session.selectedIds) {
        const row = rows.find((r) => r.card.id === id);
        const pile = piles.find((p) => p.id === row?.pileId);
        if (!pile) continue;
        const idx = pile.cards.findIndex((c) => c.id === id);
        if (idx === -1) continue;
        const [removed] = pile.cards.splice(idx, 1);
        const banished = host.findOrCreateBanished(!!pile.rotated);
        banished.cards.push(removed);
        banishedNames.push(removed.name || 'a spell');
        touched.add(pile);
        touched.add(banished);
      }
      host.updatePileMeshes();
      host.logAction('{source}: banishes {names}', { source: session.sourceName, names: banishedNames.join(' and ') });
      const source = host._findCardById(session.sourceCardId) || { id: session.sourceCardId };
      const drawArgs = session.meta?.drawArgs || { amount: 1, source: 'spell' };
      const n = host._executeDrawOp(drawArgs, source, session.controller);
      if (n > 0) host.logAction('{source}: draws {n}', { source: session.sourceName, n });
      endSession(host);
    },
  },
  // Flanking Maneuver: "any number of allies at one location." Pick the
  // subset of the source cell's allied units to teleport (minSelect 0).
  // Eligibility is the session's allowlist (every ally at the source), so
  // validate trivially accepts. The teleport + chained draw live in the
  // runtime resolver.
  flanking_subset: {
    validate() { return true; },
    confirm(host, session) {
      finishFlankingSubset(host, session);
      endSession(host);
    },
  },
};

function endSession(host, after) {
  // A cast Magic that resolved via this pile-select (Return to Nature,
  // Black Mass) is still in `_spellPlayInFlight`. The universal dispose
  // path skips while `pendingPileSelect` references it as the source. Once
  // the session clears, route the spell to the cemetery via the standard
  // dispose (a no-op for non-spell sources / cards not in flight).
  const session = host.state.pendingPileSelect;
  const spellId = session?.sourceCardId;
  const controller = session?.controller;
  host.setState({ pendingPileSelect: null, searchPile: null, searchQuery: '' }, () => {
    after?.();
    if (spellId) host._maybeDisposeFinishedSpell?.(spellId, controller);
    // An end-of-turn pick (Xeraphine) may be the last thing holding a
    // deferred sandbox turn pass open.
    host._maybeResumeDeferredTurnPass?.();
  });
}

/** Open the dialog on `pile` and begin a selection session. */
export function startPileMultiSelect(host, pile, session) {
  if (!pile) return;
  host.setState({
    searchPile: pile,
    searchQuery: '',
    pendingPileSelect: {
      selectedIds: [],
      eligibleIds: null,
      meta: {},
      ...session,
      pileId: pile.id,
    },
  });
}

/** Tile click / right-click. Toggle the card in or out. */
export function togglePileSelectCard(host, card) {
  const session = host.state.pendingPileSelect;
  if (!session) return;
  const kind = KINDS[session.kind];
  if (!kind) return;
  if (session.eligibleIds && !session.eligibleIds.includes(card.id)
      && !session.selectedIds.includes(card.id)) {
    return;
  }
  if (!session.selectedIds.includes(card.id) && !kind.validate(host, card, session)) {
    if (kind.invalidMessage) toast.error(t(kind.invalidMessage));
    return;
  }
  const next = toggleSelection(session.selectedIds, card.id, session.max);
  if (next === null) {
    toast.message(t('Already chose {n}. Unselect one first.', { n: session.max }));

    return;
  }
  host.setState({ pendingPileSelect: { ...session, selectedIds: next } });
}

/** Accept button. Commit (or advance) the session. */
export function confirmPileSelect(host) {
  const session = host.state.pendingPileSelect;
  if (!session) return;
  // `minSelect` defaults to `max` (exactly-N sessions, e.g. Xeraphine).
  // "Up to N" / "any number" sessions set minSelect: 0 so Accept fires
  // with any selection count from 0 up to max.
  const min = session.minSelect ?? session.max;
  if (session.selectedIds.length < min || session.selectedIds.length > session.max) return;
  KINDS[session.kind]?.confirm(host, session);
}

/** Dialog closed mid-session. Free abort, nothing was committed. */
export function cancelPileSelect(host) {
  const session = host.state.pendingPileSelect;
  if (!session) return;
  // A SERVER pile prompt can't just be dropped locally. The durable
  // pendingChoice would stay open invisibly. Closing the dialog DECLINES
  // (null): for an up-to-N prompt the server still runs the trailing draw;
  // for a cost prompt (Sphinx's banish pair) the effect fizzles.
  // A MANDATORY prompt (min > 0) can't be declined at all. The server would
  // no-op the null (no clearing diff), leaving the prompt hidden until the
  // next fold. Keep the dialog up instead.
  if (session.kind === 'server_pile_choice') {
    if ((session.minSelect || 0) > 0) return;
    resolveServerPileChoice(host, session, null);
    return;
  }
  host.logAction('{source}: selection cancelled', { source: session.sourceName });
  // A cast Magic that opened this session (Black Mass, Return to Nature)
  // is still in `_spellPlayInFlight`. The universal dispose skips while
  // `pendingPileSelect` references it as the source, so a cancelled
  // in-flight spell would otherwise NEVER reach the cemetery and vanish.
  // Capture the source BEFORE clearing, then dispose (a no-op for
  // non-spell sources / cards not in flight). Exactly as endSession does.
  const spellId = session.sourceCardId;
  const controller = session.controller;
  // handlePileSearchClose owns clearing searchPile; just drop the
  // session and let the deferred-pass gate re-check.
  host.setState({ pendingPileSelect: null }, () => {
    if (spellId) host._maybeDisposeFinishedSpell?.(spellId, controller);
    host._maybeResumeDeferredTurnPass?.();
  });
}
